import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Globe, ExternalLink, Trash2, Eye, EyeOff, Lock, Copy, Check, Settings, BarChart2, Sparkles } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Input, Card } from '@brix/ui'
import { tenantCanViewAnalytics, tenantCanUseAiAssistant } from '@brix/types'
import type { Json } from '@brix/types'
import { useSites, useCreateSite, useDeleteSite, useToggleSitePublished, useUpdateSiteSettings } from '@/hooks/useSites'
import { useGenerateAiPage } from '@/hooks/useAiAssistant'
import { useAuthStore } from '@/store/authStore'
import { createSiteSchema, type CreateSiteDTO } from '@/application/dto'
import { Site } from '@/domain/model/Site'
import { TemplatePicker } from '@/components/TemplatePicker'
import { createPagePort } from '@/lib/container'
import { supabase } from '@/infrastructure/supabase/client'

const SITES_URL = (import.meta.env['VITE_SITES_URL'] as string | undefined) ?? 'http://localhost:3001'
const APP_DOMAIN = (import.meta.env['VITE_APP_DOMAIN'] as string | undefined) ?? 'brix.app'

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  const copy = () => {
    void navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <button
      onClick={copy}
      className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
      title="Copy URL"
    >
      {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
    </button>
  )
}

function CreateSiteModal({ onClose }: { onClose: () => void }) {
  const createSite = useCreateSite()
  const generateAiPage = useGenerateAiPage()
  const tenant = useAuthStore((s) => s.tenant)
  const canUseAi = !!tenant && tenantCanUseAiAssistant(tenant)
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null)
  const [aiPrompt, setAiPrompt] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [aiError, setAiError] = useState<string | null>(null)
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<CreateSiteDTO>({
    resolver: zodResolver(createSiteSchema),
  })

  const subdomain = watch('subdomain', '')

  const onSubmit = async (data: CreateSiteDTO) => {
    setSubmitting(true)
    setAiError(null)
    try {
      const site = await createSite.mutateAsync(data)
      if (aiPrompt.trim() && canUseAi && site?.id) {
        try {
          await generateAiPage.mutateAsync({ siteId: site.id, prompt: aiPrompt.trim() })
        } catch (e) {
          // Site was already created successfully — surface the AI failure but don't block closing.
          setAiError(e instanceof Error ? e.message : 'AI generation failed')
          return
        }
      } else if (selectedTemplate && site?.id && tenant) {
        const page = await createPagePort.execute({
          tenantId: tenant.id,
          siteId: site.id,
          title: 'Home',
          slug: '/',
        })
        const { data: tpl } = await supabase
          .from('templates')
          .select('schema')
          .eq('id', selectedTemplate)
          .single()
        if (tpl) {
          const blocks: Array<{ type: string; props: Record<string, unknown>; settings: Record<string, unknown>; orderIndex: number }> =
            (tpl.schema as unknown as { blocks: Array<{ type: string; props: Record<string, unknown>; settings: Record<string, unknown>; orderIndex: number }> }).blocks ?? []
          if (blocks.length > 0) {
            await supabase.from('blocks').insert(
              blocks.map((b) => ({
                page_id: page.id,
                type: b.type,
                props: b.props as unknown as Json,
                settings: b.settings as unknown as Json,
                order_index: b.orderIndex,
              }))
            )
          }
        }
      }
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  const generating = generateAiPage.isPending
  const buttonLabel = generating
    ? 'Generating with AI…'
    : aiPrompt.trim() && canUseAi
      ? 'Create site with AI'
      : selectedTemplate
        ? 'Create site with template'
        : 'Create site'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <Card padding="lg" className="w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
        <h2 className="mb-6 text-lg font-bold text-gray-900">Create new site</h2>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input
            label="Site name"
            placeholder="My Portfolio"
            {...register('name', {
              onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                setValue('subdomain', e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')),
            })}
            error={errors.name?.message}
          />
          <Input
            label="Subdomain"
            placeholder="my-portfolio"
            hint={subdomain ? `URL: ${SITES_URL}/${subdomain}` : undefined}
            {...register('subdomain')}
            error={errors.subdomain?.message}
          />

          <div className="border-t border-gray-100 pt-4">
            <div className="mb-2 flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5 text-violet-500" />
              <p className="text-sm font-medium text-gray-700">Generate with AI</p>
              {!canUseAi && (
                <Link
                  to="/billing"
                  className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded font-medium hover:bg-amber-100 transition-colors"
                >
                  Pro
                </Link>
              )}
            </div>
            {canUseAi ? (
              <textarea
                aria-label="Generate with AI"
                value={aiPrompt}
                onChange={(e) => { setAiPrompt(e.target.value); if (e.target.value) setSelectedTemplate(null) }}
                placeholder='Describe your site, e.g. "A landing page for an artisan bakery called La Magdalena, warm and cozy tone"'
                rows={3}
                maxLength={2000}
                className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
              />
            ) : (
              <p className="text-xs text-gray-400">Describe your site in plain language and let AI build the home page for you.</p>
            )}
          </div>

          {!aiPrompt.trim() && (
            <div className="border-t border-gray-100 pt-4">
              <TemplatePicker selected={selectedTemplate} onSelect={(id) => { setSelectedTemplate(id); if (id) setAiPrompt('') }} />
            </div>
          )}

          {createSite.error && (
            <p className="text-sm text-red-600">{(createSite.error as Error).message}</p>
          )}
          {aiError && (
            <p className="text-sm text-red-600">Site created, but AI generation failed: {aiError}</p>
          )}
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" loading={submitting || generating} className="flex-1">
              {buttonLabel}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}

function SiteSettingsPanel({ site, canManageAnalytics }: { site: Site; canManageAnalytics: boolean }) {
  const updateSettings = useUpdateSiteSettings()
  const trackingEnabled = site.settings?.trackPageViews !== false

  const toggle = () => {
    if (!canManageAnalytics) return
    void updateSettings.mutateAsync({
      id: site.id,
      settings: { ...site.settings, trackPageViews: !trackingEnabled },
    })
  }

  return (
    <div className="border-t border-gray-100 px-4 py-3 bg-gray-50 rounded-b-xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart2 className="h-3.5 w-3.5 text-gray-400" />
          <span className="text-xs font-medium text-gray-600">Analytics tracking</span>
          {!canManageAnalytics && (
            <Link
              to="/billing"
              className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded font-medium hover:bg-amber-100 transition-colors"
            >
              Starter+
            </Link>
          )}
        </div>
        {canManageAnalytics ? (
          <button
            onClick={toggle}
            disabled={updateSettings.isPending}
            className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none disabled:opacity-50 ${
              trackingEnabled ? 'bg-violet-600' : 'bg-gray-200'
            }`}
            role="switch"
            aria-checked={trackingEnabled}
            aria-label="Analytics tracking"
            title={trackingEnabled ? 'Disable analytics' : 'Enable analytics'}
          >
            <span
              className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                trackingEnabled ? 'translate-x-4' : 'translate-x-0'
              }`}
            />
          </button>
        ) : (
          <Lock className="h-3.5 w-3.5 text-gray-300" />
        )}
      </div>
    </div>
  )
}

export function SitesPage() {
  const [showModal, setShowModal] = useState(false)
  const [settingsOpenId, setSettingsOpenId] = useState<string | null>(null)
  const { data: sites = [], isLoading } = useSites()
  const deleteSite = useDeleteSite()
  const togglePublished = useToggleSitePublished()
  const tenant = useAuthStore((s) => s.tenant)
  const canManageAnalytics = !!tenant && tenantCanViewAnalytics(tenant)

  return (
    <div className="p-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sites</h1>
          <p className="mt-1 text-sm text-gray-500">Manage your websites</p>
        </div>
        <Button onClick={() => setShowModal(true)}>
          <Plus className="h-4 w-4" />
          New site
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-violet-600 border-t-transparent" />
        </div>
      ) : sites.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 py-20">
          <Globe className="mb-3 h-10 w-10 text-gray-300" />
          <p className="text-sm font-medium text-gray-500">No sites yet</p>
          <Button className="mt-4" onClick={() => setShowModal(true)}>
            <Plus className="h-4 w-4" />
            Create your first site
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {sites.map((site) => {
            const settingsOpen = settingsOpenId === site.id
            return (
              <Card key={site.id} padding="none" className="group overflow-hidden">
                {/* Preview placeholder */}
                <div className="h-36 bg-gradient-to-br from-violet-100 to-violet-200 flex items-center justify-center">
                  <Globe className="h-10 w-10 text-violet-300" />
                </div>

                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{site.name}</h3>
                      {/* Path URL — always available */}
                      <div className="flex items-center gap-1 mt-0.5">
                        <p className="text-xs text-violet-600 truncate">{SITES_URL}/{site.subdomain}</p>
                        <CopyButton text={`${SITES_URL}/${site.subdomain}`} />
                      </div>
                      {/* Subdomain URL — requires custom domain on Cloudflare */}
                      <div className="flex items-center gap-1 mt-0.5 opacity-40" title="Requiere dominio propio en Cloudflare">
                        <Lock className="h-3 w-3 text-gray-400 shrink-0" />
                        <p className="text-xs text-gray-400 truncate">{site.subdomain}.{APP_DOMAIN}</p>
                      </div>
                    </div>
                    <span className={`ml-2 shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${
                      site.published ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {site.published ? 'Live' : 'Draft'}
                    </span>
                  </div>

                  <div className="mt-4 flex gap-2">
                    <Link
                      to={`/sites/${site.id}/pages`}
                      className="flex-1 text-center text-xs font-medium py-1.5 rounded-lg bg-violet-50 text-violet-700 hover:bg-violet-100 transition-colors"
                    >
                      Edit pages
                    </Link>
                    <button
                      onClick={() => togglePublished.mutate({ id: site.id, published: !site.published })}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
                      title={site.published ? 'Unpublish' : 'Publish'}
                    >
                      {site.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                    <a
                      href={`${SITES_URL}/${site.subdomain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                      title="View site"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                    <button
                      onClick={() => setSettingsOpenId(settingsOpen ? null : site.id)}
                      className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
                        settingsOpen
                          ? 'text-violet-600 bg-violet-50'
                          : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                      }`}
                      title="Site settings"
                    >
                      <Settings className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Delete "${site.name}"? This cannot be undone.`)) {
                          deleteSite.mutate(site.id)
                        }
                      }}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {settingsOpen && (
                  <SiteSettingsPanel site={site} canManageAnalytics={canManageAnalytics} />
                )}
              </Card>
            )
          })}
        </div>
      )}

      {showModal && <CreateSiteModal onClose={() => setShowModal(false)} />}
    </div>
  )
}
